import React from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export const Quotation = ({ data, onClose }) => {
  const {
    quotationNumber,
    customerName,
    customerPhone,
    routeName,
    travelDate,
    vehicleType,
    days,
    kmPerDay,
    perKmRate,
    baseFare,
    driverAllowance,
    permit,
    toll,
    parking,
    extraNotes,
    totalEstimate,
    createdBy,
  } = data;

  const handlePrint = () => window.print();

  // Normalize phone for wa.me link
  const buildWhatsAppPhone = (raw) => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length > 10) return digits;
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  const handleSendWhatsApp = () => {
    const phone = buildWhatsAppPhone(customerPhone);
    if (!phone) {
      toast.error('Customer phone is missing — cannot send via WhatsApp.');
      return;
    }
    const lines = [
      `Hello ${customerName || 'there'},`,
      '',
      `Here is your trip *quotation* from *SG Cabs*:`,
      '',
      `*Quotation:* ${quotationNumber}`,
      routeName ? `*Route:* ${routeName}` : '',
      travelDate ? `*Travel Date:* ${travelDate}` : '',
      vehicleType ? `*Vehicle:* ${vehicleType}` : '',
      `*Duration:* ${days} day(s) × ${kmPerDay} km/day`,
      `*Per km Rate:* ₹${perKmRate}`,
      '',
      `*Base Fare:* ${formatINR(baseFare)}`,
      Number(driverAllowance) > 0 ? `*Driver Allowance:* ${formatINR(driverAllowance)}` : '',
      Number(permit) > 0 ? `*Permit:* ${formatINR(permit)}` : '',
      Number(toll) > 0 ? `*Toll (est.):* ${formatINR(toll)}` : '',
      Number(parking) > 0 ? `*Parking (est.):* ${formatINR(parking)}` : '',
      `*Estimated Total:* ${formatINR(totalEstimate)}`,
      '',
      `_Note: Driver allowance, permit, toll and parking are estimated. Only the base fare is fixed._`,
      '',
      `Reply to confirm your booking. Thank you!`,
      `— SG Cabs | +91 75078 67648 | sg-cabs.com`,
    ].filter(Boolean).join('\n');

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp…');
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div id="quotation-print-area-overlay" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div id="quotation-print-area-wrapper" className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header with actions - hidden in print */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 print:hidden">
            <h2 className="text-xl font-bold text-gray-900">Trip Quotation</h2>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleSendWhatsApp}
                data-testid="quotation-send-whatsapp-btn"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Send via WhatsApp
              </Button>
              <Button
                onClick={handlePrint}
                data-testid="quotation-print-btn"
                className="bg-black hover:bg-gray-900 text-yellow-400"
              >
                <Printer className="w-4 h-4 mr-2" />
                Save PDF
              </Button>
              <Button onClick={onClose} variant="outline" className="border-gray-300" data-testid="quotation-close-btn">
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>

          {/* Branded Quotation Content */}
          <div id="quotation-print-area" className="bg-white">
            {/* Top brand band */}
            <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src="/sg-cabs-logo.png"
                  alt="SG Cabs"
                  className="w-16 h-16 bg-white rounded-full p-1 object-contain"
                />
                <div>
                  <h1 className="text-3xl font-extrabold tracking-tight text-yellow-400 leading-none">SG Cabs</h1>
                  <p className="text-xs text-gray-300 mt-1">Your Journey, Our Priority</p>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-block bg-yellow-400 text-black px-3 py-1 rounded-md text-xs font-bold tracking-wide">QUOTATION</div>
                <p className="text-lg font-bold mt-2">{quotationNumber}</p>
                <p className="text-xs text-gray-300">
                  Generated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                {createdBy ? (
                  <p className="text-xs text-yellow-300 mt-1" data-testid="quotation-created-by">Created by: {createdBy}</p>
                ) : null}
              </div>
            </div>

            {/* Body */}
            <div className="px-8 py-6">
              {/* Customer + Trip overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border-l-4 border-yellow-400 pl-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Quotation For</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{customerName || '—'}</p>
                  {customerPhone ? <p className="text-sm text-gray-600">Phone: {customerPhone}</p> : null}
                </div>
                <div className="border-l-4 border-yellow-400 pl-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Trip Details</p>
                  {routeName ? <p className="text-base font-semibold text-gray-900 mt-1">{routeName}</p> : <p className="text-base text-gray-400 mt-1">—</p>}
                  {travelDate ? <p className="text-sm text-gray-600">Travel Date: {travelDate}</p> : null}
                  {vehicleType ? <p className="text-sm text-gray-600">Vehicle: {vehicleType}</p> : null}
                </div>
              </div>

              {/* Fare Card */}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">Fare Breakdown</div>

                {/* Base fare row with formula */}
                <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">Base Fare</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {kmPerDay} km × {days} day(s) × ₹{perKmRate}/km
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatINR(baseFare)}</p>
                    <p className="text-[10px] text-gray-500 mt-1 italic">Fixed</p>
                  </div>
                </div>

                {Number(driverAllowance) > 0 ? (
                  <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <p className="text-gray-800">Driver Allowance</p>
                    <p className="text-gray-900 font-medium">{formatINR(driverAllowance)}</p>
                  </div>
                ) : null}
                {Number(permit) > 0 ? (
                  <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <p className="text-gray-800">Permit Charges</p>
                    <p className="text-gray-900 font-medium">{formatINR(permit)}</p>
                  </div>
                ) : null}
                {Number(toll) > 0 ? (
                  <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <p className="text-gray-800">Toll Charges <span className="text-xs text-gray-500">(est.)</span></p>
                    <p className="text-gray-900 font-medium">{formatINR(toll)}</p>
                  </div>
                ) : null}
                {Number(parking) > 0 ? (
                  <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <p className="text-gray-800">Parking Charges <span className="text-xs text-gray-500">(est.)</span></p>
                    <p className="text-gray-900 font-medium">{formatINR(parking)}</p>
                  </div>
                ) : null}

                {/* Total */}
                <div className="bg-yellow-50 px-4 py-4 flex justify-between items-center">
                  <p className="text-lg font-bold text-gray-900">Estimated Total</p>
                  <p className="text-2xl font-extrabold text-gray-900">{formatINR(totalEstimate)}</p>
                </div>
              </div>

              {/* Estimation note */}
              <div className="mt-5 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-md p-4">
                <p className="text-sm text-gray-800">
                  <span className="font-semibold">Note:</span> This is an estimation. Only the <span className="font-semibold">Base Fare</span> is fixed.
                  Driver allowance, permit, toll and parking charges are indicative and may vary based on actual usage.
                </p>
              </div>

              {extraNotes ? (
                <div className="mt-4 rounded-md border border-gray-200 p-4 bg-gray-50">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Additional Notes</p>
                  <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{extraNotes}</p>
                </div>
              ) : null}

              {/* Why SG Cabs */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-md border border-gray-200 p-3 text-center">
                  <p className="text-lg font-bold text-gray-900">4.9★</p>
                  <p className="text-xs text-gray-600">10,000+ Happy Customers</p>
                </div>
                <div className="rounded-md border border-gray-200 p-3 text-center">
                  <p className="text-lg font-bold text-gray-900">24/7</p>
                  <p className="text-xs text-gray-600">Round-the-clock Service</p>
                </div>
                <div className="rounded-md border border-gray-200 p-3 text-center">
                  <p className="text-lg font-bold text-gray-900">Pan-India</p>
                  <p className="text-xs text-gray-600">Outstation Trips Available</p>
                </div>
              </div>
            </div>

            {/* Footer band */}
            <div className="bg-gray-900 text-gray-300 px-8 py-4 text-center text-xs">
              <p className="font-semibold text-yellow-400 text-sm">SG Cabs — Premium Cab Service in Bangalore</p>
              <p className="mt-1">+91 75078 67648 &nbsp;|&nbsp; sgcabs.official@gmail.com &nbsp;|&nbsp; sg-cabs.com</p>
              <p className="mt-1 text-gray-400">Bangalore, Karnataka</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            overflow: visible !important;
          }
          body > *:not(#quotation-print-area-overlay):not(style):not(script) {
            display: none !important;
          }
          body * {
            visibility: hidden !important;
          }
          #quotation-print-area,
          #quotation-print-area * {
            visibility: visible !important;
          }
          #quotation-print-area-overlay,
          #quotation-print-area-wrapper {
            position: static !important;
            inset: auto !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            width: auto !important;
            max-width: none !important;
            height: auto !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
            display: block !important;
          }
          #quotation-print-area {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
            overflow: visible !important;
            display: block !important;
          }
          .print\\:hidden { display: none !important; }
          img { display: block !important; }
          @page {
            margin: 0.7cm;
            size: A4;
          }
        }
      `}</style>
    </>,
    document.body
  );
};
